import SimpleDrawer from '../../shared/simple-drawer';
import { Policy } from '../../../types';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getAllPoliciesQuery } from '../../api/users-api';
import { useUserSettings } from '../../utils/use-user-settings';
import ModelDetailsDisplay from '../../shared/model-details-display';
import SharedActionAccordion from '../../shared/shared-action-accordion';
import { policyActions } from '../../shared/policy-actions';
import AvatarChip from '../../shared/avatar-chip';
import { policyNarratives } from './policy-review-narratives';
import { formatPolicyScheduleTimes, transformActionHash } from './policy-utils';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { usePolicyActions } from './use-policy-actions';
import DeletePoliciesDialog from './delete-policies-dialog';
import RunPoliciesDialog from './run-policies-dialog';
import { DisabledTooltipMenuItem } from '../../shared/datagrids/tooltip-grid-actions-cell-item';
import { Messages } from '../../utils/message-constants';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Chip from '@mui/material/Chip';
import ListItemIcon from '@mui/material/ListItemIcon';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import MoreVert from '@mui/icons-material/MoreVert';
import PlayArrow from '@mui/icons-material/PlayArrow';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import Update from '@mui/icons-material/Update';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';

interface PolicyDrawerContentProps {
  selectedPolicy: Policy;
  apiHost: string;
  toolsRoles: string[];
  changeLogRoles: string[];
}

function PolicyDrawerContent({
  selectedPolicy,
  apiHost,
  toolsRoles,
  changeLogRoles,
}: Readonly<PolicyDrawerContentProps>) {
  const { closeDrawer, handlePolicyEdit, handlePolicyClone } =
    usePolicyActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [showAllTargets, setShowAllTargets] = useState(false);
  const [showAllEmails, setShowAllEmails] = useState(false);
  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    handlePolicyEdit(selectedPolicy);
  };

  const handleClone = () => {
    handleMenuClose();
    handlePolicyClone(selectedPolicy);
  };

  const handleDelete = () => {
    handleMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleRun = () => {
    handleMenuClose();
    setOpenRunDialog(true);
  };

  const renderBooleanStatusIcon = (value: boolean) =>
    value ? (
      <Check
        fontSize="small"
        sx={{ verticalAlign: 'middle', color: 'text.secondary' }}
      />
    ) : (
      <Close
        fontSize="small"
        sx={{ verticalAlign: 'middle', color: 'text.secondary' }}
      />
    );

  const mapSourceTypeToAvatarType = (
    sourceType: string | undefined
  ): 'User' | 'Group' | 'OrgUnit' | 'Domain' | 'gPanel Role' | 'Unknown' => {
    switch (sourceType) {
      case 'All_Users':
      case 'User':
        return 'User';
      case 'All_Groups':
      case 'Group':
        return 'Group';
      case 'OrgUnit':
        return 'OrgUnit';
      case 'Domain':
        return 'Domain';
      case 'gPanel_Role':
        return 'gPanel Role';
      case 'Mobile_Device':
      default:
        return 'Unknown';
    }
  };

  const basicInfoData: Record<string, React.ReactNode> = React.useMemo(() => {
    return {
      ...(selectedPolicy.createdBy && {
        'Created By': (
          <AvatarChip
            email={selectedPolicy.createdBy}
            type="User"
            label={selectedPolicy.createdBy}
          />
        ),
      }),
      'Policy Type':
        selectedPolicy.policyType === 'standard'
          ? 'Standard'
          : 'New User Created',
      'Manual Run': renderBooleanStatusIcon(Boolean(selectedPolicy.manualRun)),
      'Recurring Schedule': renderBooleanStatusIcon(
        Boolean(selectedPolicy.scheduled)
      ),
      'Set Run Date': renderBooleanStatusIcon(
        Boolean(selectedPolicy.useRunDate)
      ),
      'Policy ID': selectedPolicy.id?.toString() || 'N/A',
      ...(apiHost && {
        'API Endpoint':
          selectedPolicy.policyType === 'standard'
            ? apiHost + '/v1/policy'
            : apiHost + '/v1/policy/onboarding',
      }),
      ...(selectedPolicy.notifyViaEmail &&
        selectedPolicy.emailsToNotify &&
        selectedPolicy.emailsToNotify.length > 0 && {
          'Emails to Notify': (
            <Stack direction="column" gap={1}>
              {(showAllEmails
                ? selectedPolicy.emailsToNotify!
                : selectedPolicy.emailsToNotify!.slice(0, 3)
              ).map((email, index) => (
                <Stack
                  key={`${email}-${index}`}
                  direction="row"
                  gap={1}
                  alignItems="center"
                >
                  <AvatarChip
                    email={email}
                    label={email}
                    type="User"
                    size="small"
                  />
                  {!showAllEmails &&
                    index === 2 &&
                    selectedPolicy.emailsToNotify!.length > 3 && (
                      <Chip
                        label={`+${selectedPolicy.emailsToNotify!.length - 3}`}
                        size="small"
                        variant="outlined"
                        onClick={() => setShowAllEmails(true)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                </Stack>
              ))}
            </Stack>
          ),
        }),
    };
  }, [selectedPolicy, apiHost, showAllEmails]);

  const targetsData: Record<string, React.ReactNode> | null =
    React.useMemo(() => {
      if (selectedPolicy.policyType === 'newUser') return null;

      return {
        'Target Type': selectedPolicy.sourceType || 'N/A',
        'Target(s)':
          selectedPolicy.allSource === false &&
          selectedPolicy.sources &&
          selectedPolicy.sources.length > 0 ? (
            <Stack direction="column" gap={1}>
              {(showAllTargets
                ? selectedPolicy.sources
                : selectedPolicy.sources.slice(0, 3)
              ).map((source, index) => (
                <Stack
                  key={`${source}-${index}`}
                  direction="row"
                  gap={1}
                  alignItems="center"
                >
                  <AvatarChip
                    label={source}
                    email={source}
                    type={mapSourceTypeToAvatarType(selectedPolicy.sourceType)}
                    size="small"
                  />
                  {!showAllTargets &&
                    index === 2 &&
                    selectedPolicy.sources.length > 3 && (
                      <Chip
                        label={`+${selectedPolicy.sources.length - 3}`}
                        size="small"
                        variant="outlined"
                        onClick={() => setShowAllTargets(true)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                </Stack>
              ))}
            </Stack>
          ) : selectedPolicy.allSource ? (
            'All'
          ) : (
            'N/A'
          ),
      };
    }, [selectedPolicy, showAllTargets]);

  const getActionData = () =>
    selectedPolicy.actionHash
      ? transformActionHash(selectedPolicy.actionHash)
      : [];

  const createDetailNarrative = (action: any) => {
    if (action.type === 'Create_Gmail_Filter') {
      return 'Gmail filter will be created with the following criteria and actions:';
    }

    const narrativeFn = policyNarratives[action.type];
    return typeof narrativeFn === 'function'
      ? narrativeFn(action, selectedPolicy)
      : narrativeFn;
  };

  const scheduleData: Record<string, React.ReactNode> | null =
    React.useMemo(() => {
      if (selectedPolicy.policyType !== 'standard') return null;

      if (selectedPolicy.useRunDate && selectedPolicy.runDate) {
        return {
          'Run Date': dayjs(selectedPolicy.runDate).format(
            'MMM D, YYYY, h:mm A'
          ),
        };
      }

      if (!selectedPolicy.scheduled) return null;

      const times = selectedPolicy.times || [];
      const dates = selectedPolicy.dates || [];

      return {
        Times: formatPolicyScheduleTimes(times).join(', ') || 'N/A',
        Dates: dates.join(', ') || 'N/A',
        ...(selectedPolicy.scheduleEndDate && {
          'Expiration Date':
            dayjs(selectedPolicy.scheduleEndDate).format('MM/DD/YYYY') || 'N/A',
        }),
      };
    }, [selectedPolicy]);

  const hasRunPermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.MaunalRunPolicy.value
  );
  let runDisabledReason: string | undefined;
  if (hasRunPermission) {
    runDisabledReason =
      selectedPolicy.policyType === 'newUser'
        ? 'New User Creation policies cannot be manually run.'
        : undefined;
  } else {
    runDisabledReason = Messages.Errors.Permission.Acting(
      ToolsRoles.MaunalRunPolicy.name
    );
  }
  const editDisabledReason = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.EditPolicy.value
  )
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.EditPolicy.name);
  const cloneDisabledReason = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CopyPolicy.value
  )
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.CopyPolicy.name);
  const deleteDisabledReason = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.DeletePolicy.value
  )
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.DeletePolicy.name);

  return (
    <Stack pl={2} pr={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {selectedPolicy.policyName || 'N/A'}
        </Typography>
        <IconButton
          onClick={handleMenuClick}
          id="policy-details-menu-button"
          size="small"
        >
          <MoreVert />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          elevation={1}
          onClose={handleMenuClose}
          MenuListProps={{ 'aria-labelledby': 'policy-details-menu-button' }}
        >
          <DisabledTooltipMenuItem
            onClick={handleRun}
            disabled={
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.MaunalRunPolicy.value
              ) ||
              selectedPolicy.status === 'RUNNING' ||
              selectedPolicy.policyType === 'newUser'
            }
            data-disabled-reason={runDisabledReason}
          >
            <ListItemIcon>
              <PlayArrow fontSize="small" />
            </ListItemIcon>
            Run
          </DisabledTooltipMenuItem>
          <DisabledTooltipMenuItem
            onClick={handleEdit}
            disabled={
              !isAbleToSeeUIElement(toolsRoles, ToolsRoles.EditPolicy.value)
            }
            data-disabled-reason={editDisabledReason}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            Edit
          </DisabledTooltipMenuItem>
          <DisabledTooltipMenuItem
            onClick={handleClone}
            disabled={
              !isAbleToSeeUIElement(toolsRoles, ToolsRoles.CopyPolicy.value)
            }
            data-disabled-reason={cloneDisabledReason}
          >
            <ListItemIcon>
              <LibraryAdd fontSize="small" />
            </ListItemIcon>
            Clone
          </DisabledTooltipMenuItem>
          <DisabledTooltipMenuItem
            onClick={handleDelete}
            disabled={
              !isAbleToSeeUIElement(toolsRoles, ToolsRoles.DeletePolicy.value)
            }
            data-disabled-reason={deleteDisabledReason}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            Delete
          </DisabledTooltipMenuItem>
        </Menu>
      </Stack>

      {openRunDialog && (
        <RunPoliciesDialog
          open={openRunDialog}
          handleClose={() => setOpenRunDialog(false)}
          selectedIds={[selectedPolicy.id]}
          setSelectedIds={() => {}}
          policyList={[selectedPolicy]}
          changeLogRoles={changeLogRoles}
        />
      )}

      {openDeleteDialog && (
        <DeletePoliciesDialog
          open={openDeleteDialog}
          handleClose={() => setOpenDeleteDialog(false)}
          onDeleteSuccess={closeDrawer}
          changeLogRoles={changeLogRoles}
          selectedPolicies={[selectedPolicy.id]}
          setSelectedPolicies={() => {}}
          policyList={[selectedPolicy]}
        />
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {selectedPolicy.description || 'None Provided'}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2 }}
        sx={{ mb: 2, alignItems: 'center' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: 'text.secondary' }}
        >
          <Update fontSize="small" />
          <Typography variant="body2" color="inherit">
            Last Run:{' '}
            {selectedPolicy.lastRunDate
              ? new Date(selectedPolicy.lastRunDate).toLocaleString()
              : 'Never'}
          </Typography>
        </Stack>
        <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
          •
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: 'text.secondary' }}
        >
          <Check fontSize="small" />
          <Typography variant="body2" color="inherit">
            Created Date:{' '}
            {selectedPolicy.createdDate
              ? dayjs(selectedPolicy.createdDate).format('MMM D, YYYY, h:mm A')
              : 'N/A'}
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ pb: 4 }}>
        <ModelDetailsDisplay title="Basic Information" data={basicInfoData} />

        {targetsData && (
          <ModelDetailsDisplay title="Targets" data={targetsData} />
        )}

        {scheduleData && (
          <ModelDetailsDisplay
            title={
              selectedPolicy.useRunDate ? 'Set Run Date' : 'Recurring Schedule'
            }
            data={scheduleData}
          />
        )}

        {(() => {
          const actionData = getActionData();
          return (
            actionData &&
            actionData.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', mb: 1, fontSize: 14 }}
                >
                  Actions
                </Typography>
                <Stack spacing={2}>
                  {actionData.map((action, idx) => {
                    const actionOption = policyActions.find(
                      (x) => x.value === action.type
                    );
                    if (!actionOption) return null;
                    return (
                      <SharedActionAccordion
                        key={action.type + idx}
                        id={action.type + idx}
                        title={actionOption.label || action.type}
                        description={createDetailNarrative(action)}
                        type={actionOption.groupType}
                        expanded={true}
                        onToggleExpand={() => {}}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )
          );
        })()}
      </Box>
    </Stack>
  );
}

/**
 * Global, self-contained policy detail drawer.
 * Driven entirely by the `?selectedPolicy=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function PolicyDetailsDrawerOld() {
  const [searchParams] = useSearchParams();
  const { toolsRoles, changeLogRoles, apiUrl } = useUserSettings();
  const { closeDrawer } = usePolicyActions();

  const selectedPolicyId = searchParams.get('selectedPolicy');
  const { data: policies, isLoading } = useQuery({
    ...getAllPoliciesQuery(),
    enabled: !!selectedPolicyId,
  });
  const policyList = Array.isArray(policies) ? policies : [];
  const selectedPolicy =
    policyList.find((p) => p.id.toString() === selectedPolicyId) ?? null;

  const displayPolicy = selectedPolicy;
  const notFound = !!selectedPolicyId && !isLoading && !displayPolicy;

  return (
    <Drawer
      anchor="right"
      open={!!selectedPolicyId}
      onClose={closeDrawer}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100vw', md: '45%' } } }}
    >
      <SimpleDrawer
        title="Policy Details"
        onClose={closeDrawer}
        isLoading={isLoading}
      >
        {displayPolicy && (
          <PolicyDrawerContent
            key={displayPolicy.id}
            selectedPolicy={displayPolicy}
            apiHost={apiUrl}
            toolsRoles={toolsRoles}
            changeLogRoles={changeLogRoles}
          />
        )}
        {notFound && (
          <Typography sx={{ p: 3 }} color="error">
            Policy is not found.
          </Typography>
        )}
      </SimpleDrawer>
    </Drawer>
  );
}
